import { Editor } from '@tinymce/tinymce-react';
import { Button, Input, Modal, notification, Radio, Space } from 'antd';
import React, { useRef, useState } from 'react';
import { uploadFile } from '../../lib/axios';
import MediaPicker from './media-picker';

const editor = React.memo((prop: any) => {
	const { value, onChange, initialValue, folder } = prop;
	const urlCallback = useRef<any>(undefined);
	const editorRef = useRef<any>(undefined);
	const [showMediaSelector, setShowMediaSelector] = useState(false);
	const [showPhoneModal, setShowPhoneModal] = useState(false);
	const [phoneNumber, setPhoneNumber] = useState('');
	const [whatsappNumber, setWhatsappNumber] = useState('');
	const [phoneType, setPhoneType] = useState<'phone' | 'whatsapp' | 'both'>(
		'phone',
	);

	const imagesUploadHandler = (
		blobInfo: any,
		// progress: (percentage: number) => void,
	): Promise<string> => {
		return new Promise((resolve, reject) => {
			const file = blobInfo.blob();
			uploadFile({
				file,
				folder,
				name: blobInfo.filename(),
				watermark: false,
			})
				.then((re) => {
					if (re.data.data) {
						resolve(re.data.data.url);
						notification['info']({
							message: 'Uploading',
							description: 'File uploaded successfully!',
							duration: 2,
						});
					} else {
						reject('Upload failed');
					}
				})
				.catch((err) => {
					reject('Upload failed');
					notification['error']({
						message: 'Error',
						description: err || 'File upload failed!',
						duration: 2,
					});
				});
		});
	};

	const handlePhoneInsert = () => {
		if (!phoneNumber.trim()) {
			notification['error']({
				message: 'Error',
				description: 'Please enter a phone number',
				duration: 2,
			});
			return;
		}

		if (phoneType === 'both' && !whatsappNumber.trim()) {
			notification['error']({
				message: 'Error',
				description: 'Please enter a WhatsApp number',
				duration: 2,
			});
			return;
		}

		let content = '';
		const cleanPhone = phoneNumber.replace(/\s+/g, '');
		const cleanWhatsapp = whatsappNumber.replace(/\s+/g, '');

		switch (phoneType) {
			case 'phone':
				content = `<a href="tel:${cleanPhone}" class="phone-number">Phone Number: ${phoneNumber}</a>`;
				break;
			case 'whatsapp':
				content = `<a href="https://wa.me/${cleanPhone}" target="_blank" class="whatsapp-number">WhatsApp: ${phoneNumber}</a>`;
				break;
			case 'both':
				content = `<a href="tel:${cleanPhone}" class="phone-number">Phone Number: ${phoneNumber}</a> <a href="https://wa.me/${cleanWhatsapp}" target="_blank" class="whatsapp-number">WhatsApp: ${whatsappNumber}</a>`;
				break;
		}

		if (editorRef.current) {
			editorRef.current.insertContent(content);
		}

		setShowPhoneModal(false);
		setPhoneNumber('');
		setWhatsappNumber('');
		setPhoneType('phone');
	};

	return (
		<>
			<Modal
				zIndex={4000}
				title="Image Picker"
				open={showMediaSelector}
				footer={null}
				onCancel={() => {
					setShowMediaSelector(false);
				}}
				width={850}
			>
				{showMediaSelector ? (
					<MediaPicker
						callBack={(url) => {
							if (urlCallback.current && url) {
								urlCallback.current(url);
							}
							setShowMediaSelector(false);
						}}
					/>
				) : (
					<div></div>
				)}
			</Modal>

			<Modal
				zIndex={4000}
				title="Insert Phone Number"
				open={showPhoneModal}
				onCancel={() => {
					setShowPhoneModal(false);
					setPhoneNumber('');
					setWhatsappNumber('');
					setPhoneType('phone');
				}}
				footer={[
					<Button
						key="cancel"
						onClick={() => {
							setShowPhoneModal(false);
							setPhoneNumber('');
							setWhatsappNumber('');
							setPhoneType('phone');
						}}
					>
						Cancel
					</Button>,
					<Button key="insert" type="primary" onClick={handlePhoneInsert}>
						Insert
					</Button>,
				]}
			>
				<Space direction="vertical" style={{ width: '100%' }}>
					<div>
						<label
							style={{
								display: 'block',
								marginBottom: '8px',
								fontWeight: 'bold',
							}}
						>
							Phone Number:
						</label>
						<Input
							placeholder="Enter phone number (e.g., +971501234567)"
							value={phoneNumber}
							type="number"
							onChange={(e) => setPhoneNumber(e.target.value)}
							onPressEnter={handlePhoneInsert}
						/>
					</div>
					{phoneType === 'both' && (
						<div>
							<label
								style={{
									display: 'block',
									marginBottom: '8px',
									fontWeight: 'bold',
								}}
							>
								WhatsApp Number:
							</label>
							<Input
								placeholder="Enter WhatsApp number (e.g., +971501234567)"
								value={whatsappNumber}
								type="number"
								onChange={(e) => setWhatsappNumber(e.target.value)}
								onPressEnter={handlePhoneInsert}
							/>
						</div>
					)}
					<div>
						<label
							style={{
								display: 'block',
								marginBottom: '8px',
								fontWeight: 'bold',
							}}
						>
							Type:
						</label>
						<Radio.Group
							value={phoneType}
							onChange={(e) => setPhoneType(e.target.value)}
						>
							<Space direction="vertical">
								<Radio value="phone">Regular Phone Call</Radio>
								<Radio value="whatsapp">WhatsApp</Radio>
								<Radio value="both">Both (Phone & WhatsApp)</Radio>
							</Space>
						</Radio.Group>
					</div>
				</Space>
			</Modal>

			<Editor
				apiKey={'l55t5a5he4onuyxrd6au0sk1qhqtvk9q033vxd8ol98tf694'}
				initialValue={initialValue}
				value={value}
				onInit={(evt, editor) => {
					editorRef.current = editor;
				}}
				init={{
					width: '100%',
					height: 400,
					block_unsupported_drop: false,
					plugins: [
						'advlist',
						'accordion',
						'autosave',
						'media',
						'table',
						'image',
						'anchor',
						'autolink',
						'charmap',
						'directionality',
						'emoticons',
						'insertdatetime',
						'link',
						'lists',
						'nonbreaking',
						'pagebreak',
						'preview',
						'visualblocks',
						'visualchars',
						'fullscreen',
						'wordcount',
						'help',
						'code',
						'pageembed',
					],
					setup: (editor) => {
						editor.ui.registry.addButton('phone-number', {
							text: 'Phone',
							icon: 'edit-block',
							tooltip: 'Insert phone number',
							onAction: () => {
								setShowPhoneModal(true);
							},
						});
					},
					autosave_interval: '30s',
					autosave_ask_before_unload: true,
					autosave_prefix: 'tinymce-autosave-{path}{query}-{id}-',
					autosave_retention: '4320m', // 3 days

					// save_onsavecallback: function() {
					// 	// Gets the content from the editor
					// 	const content = this.getContent();
					// 	// this.console.log('Saved ->', content);
					// 	localStorage.setItem('Latest Saved content', content);
					// },
					toolbar: [
						'undo redo | h1 h2 h3 h4 h5 h6 | formatselect | fontfamily fontsize |' +
							'bold italic underline strikethrough | alignleft aligncenter alignright alignjustify | copy paste selectall remove print | emoticons charmap |' +
							'bullist numlist indent outdent lineheight |' +
							'forecolor backcolor | removeformat | subscript superscript blockquote | hr | ' +
							'link unlink anchor pagebreak | image | code | media | fullscreen preview |' +
							'help restore' +
							'pageembed | phone-number',
					],

					// menubar: false,
					font_family_formats:
						'Bebas Neue=Bebas Neue; Avenir Black=Avenir Black; Avenir Heavy=Avenir Heavy; Avenir Medium=Avenir Medium; DM Sans=DM Sans; DBN=DBN;',
					content_style:
						'h1, h2, h3, h4, h5, h6 { font-family: "Bebas Neue", sans-serif; } p { font-family: "DM Sans", sans-serif; }',

					// menu: {
					// 	favs: {
					// 		title: 'My Favorites',
					// 		items: 'code visualaid | searchreplace | emoticons',
					// 	},
					// 	insert: {
					// 		title: 'Insert',
					// 		items:
					// 			'image link media codesample inserttable | charmap emoticons hr | pagebreak nonbreaking anchor toc | insertdatetime',
					// 	},
					// },
					file_picker_types: 'image',
					menubar: 'favs file edit view insert format tools table help',
					file_picker_callback: function (callback) {
						urlCallback.current = callback;
						setShowMediaSelector(true);
					},
					images_upload_handler: imagesUploadHandler,
				}}
				onEditorChange={(v) => {
					onChange(v);
				}}
			/>
		</>
	);
});

export default editor;
